import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import cn from "classnames";
import Box from "@edusogno/primitives/box";
import download from "../../assets/svg/download.svg";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

const INITIALIZE_PDF = {
  maxPages: 1,
  currentPage: 1,
};

interface AllPagesProps {
  pages: number[];
  maxPages: number;
  currentPage: number;
}
const AllPages = ({ pages }: AllPagesProps) => (
  <>
    {pages.map((page, index) => (
      <Page width={1080} height={720} key={index} pageNumber={page} />
    ))}
  </>
);

interface Props {
  fileBase64: string | null;
  className?: string;
  fileUrl?: string;
}

const PDFViewer = ({ fileBase64, className = "", fileUrl }: Props) => {
  const [pdfDetails, setPdfDetails] = useState(INITIALIZE_PDF);

  const pages = Array.from(Array(pdfDetails.maxPages).keys(), (x) => x + 1);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setPdfDetails((prev) => ({ ...prev, maxPages: numPages }));
  }

  const handleDownload = () => {
    if (!fileBase64) return;

    try {
      const byteCharacters = atob(fileBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      // Create an anchor element and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = "document.pdf"; // Provide a default filename
      document.body.appendChild(link);
      link.click(); // Simulate a click to trigger the download
      document.body.removeChild(link); // Clean up

      // Cleanup the URL object
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <>
      {fileUrl ? (
        <iframe
          src={fileUrl}
          className={cn("w-full h-full", className)}
          title="PDF Viewer"
        />
      ) : (
        <Box
          id="container"
          css={{
            overflowX: "auto",
            overflowY: "auto",
            height: "100%",
            width: "100%",
            justifyContent: "center",
            alignItems: "start",
            display: "flex",
            "& .react-pdf__Document": {
              display: "flex",
              overflowX: "hidden",
              overflowY: "auto",
              height: "100%",
              width: "100%",
            },
          }}
          className={cn(className)}
        >
          <Document
            file={`data:application/pdf;base64,${fileBase64}`}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            <div className="flex items-center justify-center w-full relative">
              <div className="relative flex flex-col items-center h-full overflow-y-scroll">
                <AllPages
                  pages={pages}
                  maxPages={pdfDetails.maxPages}
                  currentPage={pdfDetails.currentPage}
                />
              </div>
              <div className="absolute left-3 bottom-3 h-[35px] w-[35px]  z-10">
                <button
                  onClick={handleDownload}
                  className="h-[35px] w-[35px] bg-[#6941C6] rounded-[20%] opacity-20 hover:opacity-100 hover:cursor-pointer transition-opacity duration-300 flex justify-center items-center"
                >
                  <img
                    src={download}
                    alt="download"
                    className="w-[21px] h-[21px] rounded-[20%]"
                  />
                </button>
              </div>
            </div>
          </Document>
        </Box>
      )}
    </>
  );
};

export default PDFViewer;
